/**
 * Waveform real de áudio (bolha de voz + gravador).
 *
 * - `decodeWaveform`: busca o áudio, decodifica via Web Audio
 *   (`decodeAudioData`) e devolve ~40 picos normalizados (0..1) por RMS por
 *   balde. Retorna `null` quando a decodificação falha — o chamador cai para
 *   `deterministicWaveform` (forma estável por `seed`, ex. `message.id`).
 * - `deterministicWaveform`: fallback estável/determinístico (0..1).
 * - `formatAudioTime`: `m:ss` para duração decorrida/total.
 */

export const WAVEFORM_BARS = 40;

/** Duração máxima do PTT (docs/09 RF-UX-030): 2:00. */
export const VOICE_MAX_DURATION_MS = 120_000;

/** Valida `durationMs`: 0 < d ≤ 120000. Devolve ms arredondado ou `undefined`. */
export function sanitizeDurationMs(value: unknown): number | undefined {
  const n = typeof value === "string" ? Number(value) : (value as number);
  if (typeof n !== "number" || !Number.isFinite(n)) return undefined;
  const ms = Math.round(n);
  if (ms <= 0 || ms > VOICE_MAX_DURATION_MS) return undefined;
  return ms;
}

export function formatAudioTime(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) return "0:00";
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Forma determinística estável por seed (fallback quando o decode falha). */
export function deterministicWaveform(seed: string, count = WAVEFORM_BARS): number[] {
  let hash = 2166136261;
  const bars: number[] = [];
  const safe = seed.length > 0 ? seed : "zapir";
  for (let i = 0; i < count; i += 1) {
    hash ^= safe.charCodeAt(i % safe.length);
    hash = Math.imul(hash, 16777619);
    // 0.15..1 — nunca some (contraste da barra mais baixa).
    bars.push(0.15 + (Math.abs(hash) % 85) / 100);
  }
  return bars;
}

function peaksFromChannelData(data: Float32Array, count: number): number[] {
  const bars: number[] = [];
  const bucket = Math.max(1, Math.floor(data.length / count));
  let max = 0;
  const rms: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const start = i * bucket;
    let sum = 0;
    let n = 0;
    for (let j = start; j < Math.min(start + bucket, data.length); j += 8) {
      const v = data[j] ?? 0;
      sum += v * v;
      n += 1;
    }
    const value = n > 0 ? Math.sqrt(sum / n) : 0;
    rms.push(value);
    if (value > max) max = value;
  }
  for (const value of rms) {
    bars.push(max > 0 ? Math.max(0.12, Math.min(1, value / max)) : 0.12);
  }
  return bars;
}

type AudioContextCtor = typeof AudioContext;

function audioCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  return Ctx ?? null;
}

/**
 * Decodifica `url` e gera os picos. Usa `fetch` + `decodeAudioData`; qualquer
 * falha (rede, CORS, codec) resolve `null` para o chamador usar o fallback.
 */
export async function decodeWaveform(
  url: string,
  count = WAVEFORM_BARS,
  signal?: AbortSignal,
): Promise<{ bars: number[]; durationSec: number } | null> {
  try {
    const Ctx = audioCtor();
    if (!Ctx) return null;
    const response = await fetch(url, { signal });
    if (!response.ok) return null;
    const buffer = await response.arrayBuffer();
    if (signal?.aborted) return null;
    const ctx = new Ctx();
    try {
      const decoded = await ctx.decodeAudioData(buffer.slice(0));
      if (signal?.aborted) return null;
      const channel = decoded.getChannelData(0);
      if (!channel) return null;
      return { bars: peaksFromChannelData(channel, count), durationSec: decoded.duration };
    } finally {
      void ctx.close().catch(() => undefined);
    }
  } catch {
    return null;
  }
}

/**
 * Waveform que o proprio WhatsApp manda no payload: 64 bytes 0..100 em base64.
 * Usar ela evita baixar o audio uma segunda vez so para desenhar as barras.
 * Reamostra para `count` barras e normaliza 0..1 pelo pico (audio baixinho
 * vira uma linha reta se normalizar por 100 fixo).
 */
export function waveformFromBase64(value: string, count = WAVEFORM_BARS): number[] | null {
  let bytes: Uint8Array;
  try {
    const bin = atob(value);
    bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
  if (bytes.length < 8) return null;
  const peak = Math.max(...bytes);
  if (peak <= 0) return null;
  const bars: number[] = [];
  for (let i = 0; i < count; i += 1) {
    // Media do balde: o WhatsApp manda 64 amostras e desenhamos ~40, entao
    // pegar "a mais proxima" perderia picos entre uma barra e outra.
    const start = Math.floor((i * bytes.length) / count);
    const end = Math.max(start + 1, Math.floor(((i + 1) * bytes.length) / count));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += bytes[j] ?? 0;
    bars.push(Math.max(0.08, Math.min(1, sum / (end - start) / peak)));
  }
  return bars;
}
